// Dependencies
import { useState, useEffect, useRef, useCallback, useMemo } from "react";
import { useResizeDetector } from "react-resize-detector";
import _cx from 'classnames';
import _ from 'lodash';

// Hooks
import useDebug from "../hooks/Wooptima/Debug";
import useWindowResize from '../hooks/Wooptima/WindowResize';
import useAPI from '../hooks/Api';

// Components
import Form from './Form';
import Modal from './Modal';

// Contexts
import AppContext from '../contexts/AppContext';
 
// Types
import { Types } from '../utils/types';

// Helpers
import { getCloseIcon, getBackgroundImage, getMouseIcon, getPhoneIcon, getEmailIcon } from '../utils/helpers';
 
const App = () => {

    // Component | States
    const [isFormOpening, setIsFormOpening]             = useState<boolean>(false);
    const [isFormClosing, setIsFormClosing]             = useState<boolean>(false);
    const [isFormLoading, setIsFormLoading]             = useState<boolean>(true);
    
    const [isFormOpened, setIsFormOpened]               = useState<boolean>(false);
    const [isFormFocused, setIsFormFocused]             = useState<boolean>(false);
    const [isFormScrollable, setIsFormScrollable]       = useState<boolean>(false);
    const [isFormValid, setIsFormValid]                 = useState<boolean>(false);

    const [formSections, setFormSections]               = useState<Types.FormSection[]>([])

    const [isCloseModalOpened, setIsCloseModalOpened]   = useState<boolean>(false);
    const [isFormSuccessModalOpened, setIsFormSuccessModalOpened]     = useState<boolean>(false);
    const [isFormErrorModalOpened, setIsFormErrorModalOpened]     = useState<boolean>(false);

    // Component | Refs
    const siteHeaderRef = useRef<any>(null);
    const formRef = useRef<any>(null);
    
    const prevFieldRef = useRef<any>(null);
    const currentFieldRef = useRef<any>(null);
    const nextFieldRef = useRef<any>(null);

    // Component | Hooks
    const { getFormSections } = useAPI();
    const { logDebugMessage } = useDebug('App');
    const { windowWidth, windowHeight } = useWindowResize(true);

    // Component | Helpers
    const closeForm = () => {

        requestAnimationFrame(() => {
            setIsFormValid(false);
            setIsFormScrollable(false);
            setIsFormClosing(true);
            setIsFormFocused(false);
            setIsFormOpened(false);
            formRef.current.resetForm();
        })
        
        setTimeout(() => {
            requestAnimationFrame(() => {
                setIsFormClosing(false);
            })
        }, 1000);

    }

    const handleSubmit = ({data: response}: any) => {

        if ( ! _.isEmpty(response.errors) || ! response.isValid ) {
            setIsFormErrorModalOpened(true);
            setIsFormValid(false);
        } else {
            setIsFormSuccessModalOpened(true);
        }
 
    }

    // Resize Detector | .app__header 
    const { ref: appHeaderRef } = useResizeDetector({        
        handleWidth: false,
        handleHeight: true,
        refreshMode: 'throttle',
        refreshRate: 250,
        onResize: useCallback(() => {

            if ( ! (appHeaderRef.current instanceof HTMLElement) ) {
                return;
            }
        
            const headerStyles = getComputedStyle( appHeaderRef.current );
            const headerHeight = ['height', 'margin-top', 'margin-bottom']
                .map((key) => parseInt(headerStyles.getPropertyValue(key), 10))
                .reduce((prev, cur) => prev + cur);
        
            document.documentElement.style.setProperty('--app-header-height', `${headerHeight}px`);
        
        }, [])
    });

    useResizeDetector({      
        targetRef: siteHeaderRef,  
        handleWidth: false,
        handleHeight: true,
        refreshMode: 'throttle',
        refreshRate: 250,
        onResize: useCallback(() => {

            if ( ! (siteHeaderRef.current instanceof HTMLElement) ) {
                return;
            }
        
            const headerStyles = getComputedStyle( siteHeaderRef.current );
            const headerHeight = ['height', 'margin-top', 'margin-bottom']
                .map((key) => parseInt(headerStyles.getPropertyValue(key), 10))
                .reduce((prev, cur) => prev + cur);
        
            document.documentElement.style.setProperty('--site-header-height', `${headerHeight}px`);
        
        }, [])
    })

    // Get formSections data from the API
    useEffect(() => {

        siteHeaderRef.current = document.querySelector('header.site-header') || null;
        
        const metaViewport = document.querySelector('meta[name=viewport]') || null;
        
        if ( metaViewport instanceof HTMLMetaElement && ! metaViewport.getAttribute('content')?.includes('user-scalable=no') ) {
            metaViewport?.setAttribute('content', `${metaViewport.getAttribute('content')}, user-scalable=no`);
        }

        getFormSections()
        .then( (response) => {
						 // @ts-ignore
            setFormSections( response?.data );
            setIsFormLoading(false);
        })
        .catch((errors) => {
            // TODO: Handle errors
            setIsFormErrorModalOpened(true);
            logDebugMessage(errors);
        })

    }, []);
 

    // Toggle "es-scroll--disabled" class on body element (overflow: hidden) when "isFormFocused" state changes
    useEffect(() => {

        if ( isFormFocused ) {

            requestAnimationFrame(() => {
                setIsFormOpening(true);
            })
    
            setTimeout(() => {
                requestAnimationFrame(() => {
                    setIsFormOpening(false);
                    setIsFormOpened(true);
                })
            }, 1000);
            
        } else {
            setTimeout(() => {
                requestAnimationFrame(() => {
                    setIsFormOpened(false);
                })
            }, 1000);
        }
        
        window.scrollTo({top: 0, behavior: 'smooth'});
        document.body.classList.toggle('es-scroll--disabled', isFormFocused);

    }, [isFormFocused])

    const appClassNames = useMemo(() => {
        return {
            'is-scrollable': isFormScrollable,
            'is-focused': isFormFocused,
            'is-valid': isFormValid,
            'is-opened': isFormOpened,
            'is-opening': isFormOpening,
            'is-closing': isFormClosing,
            'is-loading': isFormLoading,
        }
    }, [isFormScrollable, isFormFocused, isFormValid, isFormOpening, isFormClosing, isFormLoading, isFormOpened]);

    return (
        <AppContext.Provider value={ 
            {
                isLoading: isFormLoading,
                isFocused: isFormFocused,
                isScrollable: isFormScrollable, 
                isValid: isFormValid, 
                toggleLoading: (force: boolean|undefined)   => setIsFormLoading( !_.isUndefined(force) ? force : isFormLoading ),
                toggleFocus: (force: boolean|undefined)     => setIsFormFocused( !_.isUndefined(force) ? force : isFormFocused ),
                toggleScroll: (force: boolean|undefined)    => setIsFormScrollable( !_.isUndefined(force) ? force : isFormFocused ),
                toggleValid: (force: boolean|undefined)     => setIsFormValid( !_.isUndefined(force) ? force : isFormValid ),
                refs: {
                    prevField: prevFieldRef,
                    currentField: currentFieldRef,
                    nextField: nextFieldRef,
                }
            }}>
            <div className={_cx('app__wrapper', appClassNames)} style={{backgroundImage: `url(${getBackgroundImage()})`}}>

                <Modal 
                    id={'confirm-close'}
                    type="confirm" 
                    heading={'Ste si istý, že chcete zavrieť formulár?'} 
                    description={'Všetky vyplnené údaje budú stratené.'}
                    isOpened={isCloseModalOpened}
                    onClick={(e: any) => {
                        if ( e.currentTarget.dataset.name === 'yes' ) {
                            closeForm();
                        }
                        setIsCloseModalOpened(false);
                    }}
                />

                <Modal 
                    id={'submit-success'}
                    type="info" 
                    heading={'Ďakujeme, Vaša orientačná ponuka bola odoslaná.'} 
                    description={'Náš obchodný zástupca vás bude kontaktovať do 24 hodín.'}
                    isOpened={isFormSuccessModalOpened}
                    onClick={(e: any) => {
                        closeForm();
                        setIsFormSuccessModalOpened(false);
                    }}
                />

                <Modal 
                    id={'submit-error'}
                    type="error" 
                    heading={'Niečo sa pokazilo.'} 
                    description={'Skúste to, prosím, opäť neskôr.'}
                    isOpened={isFormErrorModalOpened}
                    onClick={(e: any) => {
                        setIsFormErrorModalOpened(false);
                    }}
                />
                    
                <div className="app__container">
        
                    <div ref={appHeaderRef} className="app__header"> 
                        <h1 className="app__header--text">
                            {windowWidth <= 768 && isFormFocused 
                                ? <span>Orientačná ponuka</span>
                                : <> <span>Spravte si pár klikmi</span> <strong className="es-text--highlighted">orientačnú ponuku</strong> </>
                            }
                        </h1>
                        {
                            (isFormFocused || isFormClosing) && 
                                <div className="wooptima-button--close" onClick={() => setIsCloseModalOpened(true)}>{getCloseIcon()}</div>
                        }
                    </div>
        
                    <div className="app__form"> 
                        <Form ref={formRef} sections={formSections} onFormSubmit={handleSubmit}/>
                    </div>

                    {/* <div className="app__footer">
                        <span className="app__footer--heading">Ozvite sa nám priamo na:</span>
                        <div className="app__footer--content">
                            <div className="contact-info phone">
                                {getPhoneIcon('contact-info-icon')}
                                <div className="contact-info-meta">
                                    <strong className="contact-info-meta__value">0850 555 999</strong>
                                    <small className="contact-info-meta__description">Po - Pi od 8:30 do 16:00</small>
                                </div>
                            </div>
                            <div className="contact-info email">
                                {getEmailIcon('contact-info-icon')}
                                <div className="contact-info-meta">
                                    <strong className="contact-info-meta__value">energy.services@seas.sk</strong>
                                </div>
                            </div>
                        </div>
                        <div className="app__footer--learn-more">
                            {getMouseIcon()}
                            <span>Zistiť viac</span>
                        </div>
                    </div> */}
        
                </div>
                
                <div className="app__overlay"></div>
                
            </div>
        </AppContext.Provider>
    );

}

export default App; 